import type { IDynamicFilterControl } from '@gp/components/DynamicFilter/components/FilterControl/types';
import FilterCommon from '@gp/components/FilterCommon';
import { KEYS_MAPPING, NAMES_MAPPING } from '@gp/constants/field';
import { COMMON_SELECT_MULTIPLE_CONTROL_CFG } from '@gp/constants/select';
import type { IRecord } from '@gp/types/global';
import type { IBaseProps } from '@home/types/common';
import { cloneDeep, isEqual, merge } from 'lodash-es';
import React, { useEffect, useMemo, useState } from 'react';

interface IFilterProps extends IBaseProps {
  loading?: boolean;
  data?: IPipelineOverviewScheduleFilterConditionsVO;
  value?: IPipelineOverviewScheduleFilterVO;
  onChange?: (payload: IPipelineOverviewScheduleFilterVO) => void;
}

const DEFAULT_CONTROLS: IRecord<IDynamicFilterControl> = {
  composedDatePicker: {
    loading: true,
    showGranularitySelect: true,
    showShortcut: true,
  },
  pteam: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    matchButtonWidth: false,
    listWidth: 260,
    options: [],
  },
  business_owner: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    matchButtonWidth: false,
    listWidth: 260,
    options: [],
  },
  studio: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    matchButtonWidth: false,
    listWidth: 260,
    options: [],
  },
  project: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    matchButtonWidth: false,
    listWidth: 260,
    options: [],
  },
  platform: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    searchable: false,
    options: [],
  },
  milestone: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    searchable: false,
    listWidth: 340,
    options: [],
  },
  publisher: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    matchButtonWidth: false,
    searchable: false,
    listWidth: 260,
    options: [],
  },
  // status: {
  //   ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
  //   searchable: false,
  //   options: [],
  // },
  stage: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    searchable: false,
    options: [],
  },
  form: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    searchable: false,
    options: [],
  },
};

const Filter: React.FC<IFilterProps> = (props) => {
  const {
    style,
    className,
    loading = true,
    data,
    value: valueProps,
    onChange: onChangeProps,
    onReset,
    allFilterOptionsValues,
    setIsOverFiler,
    scrollBody,
    headerTitle,
    headerOther,
    currentPage,
    hasSearchBtn,
    searchPlaceholder,
    formatSearchResults,
    getSearchList,
    selectedValue,
    setSelectedValue,
    searchList,
    setSearchList,
    searchVal,
    setSearchVal,
  } = props;

  const controls = useMemo<IRecord<IDynamicFilterControl>>(() => {
    if (loading) return DEFAULT_CONTROLS;
    const {
      composedDatePicker,
      platform: platformOptions,
      milestone: milestoneOptions,
      publisher: publisherOptions,
      // status: statusOptions,
      stage: stageOptions,
      form: groupOptions,
      project_id: cascadeProjectOptions,
    } = data || {};
    const { showGranularitySelect, range, defaultDate } =
      composedDatePicker || {};
    const controlsTmp = cloneDeep(DEFAULT_CONTROLS);
    return merge(controlsTmp, {
      composedDatePicker: {
        loading: false,
        showGranularitySelect,
        range,
        defaultDate,
      },
      project_id: {
        options: cascadeProjectOptions,
        allValues: allFilterOptionsValues?.project_id || [],
        btnTitle: 'P-team/BO/Studio/Project',
      },
      platform: {
        options: platformOptions,
      },
      milestone: {
        options: milestoneOptions,
      },
      publisher: {
        options: publisherOptions,
      },
      // status: {
      //   options: statusOptions,
      // },
      stage: {
        options: stageOptions,
      },
      form: {
        options: groupOptions,
      },
    });
  }, [loading, data, allFilterOptionsValues]);

  const [value, setValue] = useState<IPipelineOverviewScheduleFilterVO>({
    composedDatePicker: {
      granularity: 'quarter',
      date: ['', ''],
    },
    pteam: [],
    business_owner: [],
    studio: [],
    project: [],
    platform: [],
    milestone: [],
    publisher: [],
    status: [],
    stage: [],
    form: [],
    project_id: [],
  });

  const fields = useMemo(() => {
    const { project_id: projectOptions, status: statusOptions } = data || {};
    const projectDisplay = loading
      ? false
      : Array.isArray(projectOptions) && projectOptions.length > 0;
    const statusDisplay = loading
      ? false
      : Array.isArray(statusOptions) && statusOptions.length > 0;
    return [
      {
        key: 'composedDatePicker',
        type: 'composedDatePicker',
        display: true,
        label: '',
        saveKey: 'overview_composedDatePicker',
      },
      {
        key: KEYS_MAPPING.CASCADE_PROJECT,
        type: 'cascadeProject',
        display: projectDisplay,
        label: NAMES_MAPPING.CASCADE_PROJECT,
      },
      {
        key: KEYS_MAPPING.PLATFORM,
        type: 'selectMultiple',
        display: true,
        label: NAMES_MAPPING.PLATFORM,
      },
      // {
      //   key: KEYS_MAPPING.MILESTONE,
      //   type: 'selectMultiple',
      //   display: true,
      //   label: NAMES_MAPPING.MILESTONE,
      // },
      {
        key: KEYS_MAPPING.PUBLISHER,
        type: 'selectMultiple',
        display: true,
        label: NAMES_MAPPING.PUBLISHER,
      },
      // {
      //   key: KEYS_MAPPING.STATUS,
      //   type: 'selectMultiple',
      //   display: statusDisplay,
      //   label: NAMES_MAPPING.STATUS,
      // },
      {
        key: KEYS_MAPPING.STAGE,
        type: 'selectMultiple',
        display: true,
        label: NAMES_MAPPING.STAGE,
      },
    ];
  }, [loading, data]);

  const handleChange = (
    payload: IRecord<IPipelineOverviewScheduleFilterVO>,
  ) => {
    const valueTmp = { ...value, ...payload };
    setValue(valueTmp);
  };

  const handleConfirm = () => {
    onChangeProps?.(value);
  };

  useEffect(() => {
    if (!isEqual(value, valueProps)) {
      setValue({ ...value, ...valueProps });
    }
  }, [valueProps]);

  return (
    <FilterCommon
      styles={style}
      className={className}
      controls={controls}
      value={value}
      onChange={handleChange}
      isNeedDiff={true}
      fields={fields}
      confirm={handleConfirm}
      onReset={onReset}
      loading={loading}
      setIsOverFiler={setIsOverFiler}
      scrollBody={scrollBody}
      filterSearchHeight={110}
      filterHeight={267}
      headerTitle={headerTitle}
      headerOther={headerOther}
      currentPage={currentPage}
      hasSearchBtn={hasSearchBtn}
      searchPlaceholder={searchPlaceholder}
      formatSearchResults={formatSearchResults}
      getSearchList={getSearchList}
      selectedValue={selectedValue}
      setSelectedValue={setSelectedValue}
      searchList={searchList}
      setSearchList={setSearchList}
      searchVal={searchVal}
      setSearchVal={setSearchVal}
    />
  );
};

export default Filter;
